import React, { useState } from 'react';
import {
  Segment,
  Header,
  Button,
  Icon,
  Grid,
  Form,
  Input,
  Table,
  Dropdown,
  Label
} from 'semantic-ui-react';
import { cleanCcid, cleanPhone, mergeDddAndLocalPhoneDigits, parseRenegociacaoNumeroFieldInput, renegociacaoLocalDigitsFromFormField, toBrazilNationalPhoneDigits } from '../../services/utils/stringUtils';
import { defaultContractRules, type TeamContractRules } from '../../services/teams/teamService';
import { useToast } from '../../hooks/useToast';
import QuantitySelector from './QuantitySelector';
import PortabilidadeDadosForm from './PortabilidadeDadosForm';
import PortabilidadeConflictModal from './PortabilidadeConflictModal';
import {
  applyCedenteUnification,
  applyPortabilidadeTipoConversion,
  applyPortanteUnification,
  copyPortabilidadeFieldsFromLine,
  detectPortabilidadeConflict,
  getPortabilidadeValidationError,
  isPortabilidadeAditivoLine,
  isPortabilidadeIncompleta,
  resolvePortabilidadeTipo,
  type PortabilidadeConflict,
  type PortabilidadeRepresentanteFields,
} from '../../utils/portabilidadeContractRules';
import {
  filterPlansForContractLine,
  formatContractPlanOptionLabel,
  hasContractLineMonthlyValue,
  resolvePlanTotalMonthlyPrice,
} from '../../utils/contractPlanCompatibility';

export interface ContractLineEditorItem {
  id: string;
  negociacao?: string;
  ddd?: string;
  numero?: string;
  chipCcid?: string;
  tipoVenda?: string;
  planId?: string;
  plano?: string;
  planName?: string;
  aparelho?: string;
  valorChip?: number;
  valorMensal?: number;
  valorTotalMensal?: number;
  quantidade?: number;
  ofertas?: any[];
  ehPortabilidade?: boolean;
  portabilidadeNomeCompleto?: string;
  portabilidadeCpf?: string;
  portabilidadeOperadora?: string;
  portabilidadeLinha?: string;
  portabilidadeCnpjCedente?: string;
  portabilidadeRazaoSocialCedente?: string;
  portabilidadeRepresentantes?: PortabilidadeRepresentanteFields[];
  seraUsadaPeloCliente?: boolean | null;
  [key: string]: any;
}

export interface ContractLinesEditorProps {
  mode: 'create' | 'edit';
  linhas: ContractLineEditorItem[];
  onUpdateLinha: (
    id: string,
    field: string,
    value: string | number | boolean | PortabilidadeRepresentanteFields[]
  ) => void;
  onRemoveLinha: (id: string) => void;
  onRemoveGroup?: (planId: string, negociacao: string) => void;
  onAddLinha?: (line: Partial<ContractLineEditorItem>) => void;
  clientEstado?: string;
  fastchip?: boolean;
  dddOptions: Array<{ key: string; text: string; value: string }>;
  getValidDddsWithICcIdFallback: (estado: string, isFastchip: boolean) => string[];
  /** Create mode: render Add form as children (LinesList is inside, children = Add form) */
  children?: React.ReactNode;
  /** Create mode: use LinesList - pass duplicate helpers */
  hasDuplicateCcid?: (linhas: any[]) => boolean;
  hasDuplicatePhone?: (linhas: any[]) => boolean;
  getDuplicateCcidLines?: (linhas: any[]) => string[];
  getDuplicatePhoneLines?: (linhas: any[]) => string[];
  /** Edit mode: props for plan selection */
  plans?: any[];
  plansFiltrados?: any[];
  plansLoading?: boolean;
  onFilterPlans?: (negociacao: string, aparelho?: string) => void;
  onAddDddOption?: (ddd: string) => void;
  /** Edit mode: number of ICCIDs available for client state (Fastchip); used to disable add / validate quantity */
  availableICcIdsCount?: number;
  /** Quando false, oculta ações de criar/editar/excluir linhas. */
  allowLineMutations?: boolean;
  /** Regras de contrato do time do usuário (validação e feedback na UI). */
  teamContractRules?: TeamContractRules;
}

const defaultDuplicateHelpers = {
  hasDuplicateCcid: (linhas: any[]) => {
    const ccids = linhas.filter((l: any) => l.negociacao === 'Aditivo' && l.chipCcid).map((l: any) => l.chipCcid);
    return new Set(ccids).size !== ccids.length;
  },
  hasDuplicatePhone: (linhas: any[]) => {
    const phones = linhas
      .filter((l: any) => l.negociacao === 'Renegociação' && l.numero)
      .map((l: any) =>
        l.ddd && l.numero ? mergeDddAndLocalPhoneDigits(l.ddd, l.numero) : cleanPhone(l.numero)
      );
    return new Set(phones).size !== phones.length;
  },
  getDuplicateCcidLines: (linhas: any[]) => {
    const ccidCount: Record<string, string[]> = {};
    linhas.filter((l: any) => l.negociacao === 'Aditivo' && l.chipCcid).forEach((linha: any) => {
      if (!ccidCount[linha.chipCcid]) ccidCount[linha.chipCcid] = [];
      ccidCount[linha.chipCcid].push(linha.id);
    });
    return Object.values(ccidCount).filter((ids) => ids.length > 1).flat();
  },
  getDuplicatePhoneLines: (linhas: any[]) => {
    const phoneCount: Record<string, string[]> = {};
    linhas.filter((l: any) => l.negociacao === 'Renegociação' && l.numero).forEach((linha: any) => {
      const numeroCompleto =
        linha.ddd && linha.numero
          ? mergeDddAndLocalPhoneDigits(linha.ddd, linha.numero)
          : cleanPhone(linha.numero);
      if (!phoneCount[numeroCompleto]) phoneCount[numeroCompleto] = [];
      phoneCount[numeroCompleto].push(linha.id);
    });
    return Object.values(phoneCount).filter((ids) => ids.length > 1).flat();
  }
};

export const ContractLinesEditor: React.FC<ContractLinesEditorProps> = (props) => {
  const {
    mode,
    linhas,
    onUpdateLinha,
    onRemoveLinha,
    onRemoveGroup,
    onAddLinha,
    clientEstado = '',
    fastchip = false,
    dddOptions,
    getValidDddsWithICcIdFallback,
    children,
    plans = [],
    plansFiltrados = [],
    plansLoading = false,
    onFilterPlans,
    onAddDddOption,
    availableICcIdsCount,
    allowLineMutations = true,
    hasDuplicateCcid = defaultDuplicateHelpers.hasDuplicateCcid,
    hasDuplicatePhone = defaultDuplicateHelpers.hasDuplicatePhone,
    getDuplicateCcidLines = defaultDuplicateHelpers.getDuplicateCcidLines,
    getDuplicatePhoneLines = defaultDuplicateHelpers.getDuplicatePhoneLines,
    teamContractRules = defaultContractRules,
  } = props;

  const readOnly = !allowLineMutations;
  const { showError, showSuccess } = useToast();
  const [portabilidadeConflict, setPortabilidadeConflict] = useState<PortabilidadeConflict | null>(null);
  const [pendingAddAfterConflict, setPendingAddAfterConflict] = useState(false);

  const duplicateCcidLines = teamContractRules.blockDuplicateCcid ? getDuplicateCcidLines(linhas) : [];
  const duplicatePhoneLines = teamContractRules.blockDuplicatePhone ? getDuplicatePhoneLines(linhas) : [];
  const hasCcidDuplicates = teamContractRules.blockDuplicateCcid && hasDuplicateCcid(linhas);
  const hasPhoneDuplicates = teamContractRules.blockDuplicatePhone && hasDuplicatePhone(linhas);

  const [linhaAtual, setLinhaAtual] = useState({
    negociacao: 'Aditivo' as 'Aditivo' | 'Renegociação',
    ddd: '',
    numero: '',
    chipCcid: '',
    tipoVenda: 'Sem aparelho',
    planId: '',
    plano: '',
    aparelho: '',
    valorChip: fastchip ? 0 : 7.00,
    valorMensal: 0,
    quantidade: 1,
    ehPortabilidade: false,
    portabilidadeNomeCompleto: '',
    portabilidadeCpf: '',
    portabilidadeOperadora: '',
    portabilidadeLinha: '',
    portabilidadeCnpjCedente: '',
    portabilidadeRazaoSocialCedente: '',
    portabilidadeRepresentantes: [] as PortabilidadeRepresentanteFields[],
    seraUsadaPeloCliente: null as boolean | null,
  });

  const validDdds = getValidDddsWithICcIdFallback(clientEstado, fastchip);

  // Filter plans when negotiation changes in edit mode
  React.useEffect(() => {
    if (mode === 'edit' && onFilterPlans) {
      onFilterPlans(linhaAtual.negociacao, linhaAtual.aparelho);
    }
  }, [linhaAtual.negociacao, linhaAtual.aparelho, mode, onFilterPlans]);

  const commitAddLinha = (linesToUse = linhas) => {
    if (!onAddLinha) return;
    const numeroToUse =
      linhaAtual.negociacao === 'Renegociação' && linhaAtual.ddd
        ? renegociacaoLocalDigitsFromFormField(linhaAtual.ddd, linhaAtual.numero || '')
        : linhaAtual.numero || '';

    for (let i = 0; i < linhaAtual.quantidade; i++) {
      onAddLinha({
        id: `linha-${Date.now()}-${i}`,
        negociacao: linhaAtual.negociacao,
        ddd: linhaAtual.ddd,
        numero: linhaAtual.negociacao === 'Renegociação' ? numeroToUse : undefined,
        chipCcid: linhaAtual.negociacao === 'Aditivo' ? linhaAtual.chipCcid : undefined,
        tipoVenda: linhaAtual.tipoVenda,
        planId: linhaAtual.planId,
        plano: linhaAtual.plano,
        aparelho: linhaAtual.aparelho,
        valorChip: Number(linhaAtual.valorChip) || 0,
        valorMensal: Number(linhaAtual.valorMensal) || 0,
        quantidade: 1,
        ofertas: [],
        ehPortabilidade: linhaAtual.ehPortabilidade,
        portabilidadeNomeCompleto: linhaAtual.ehPortabilidade ? linhaAtual.portabilidadeNomeCompleto : undefined,
        portabilidadeCpf: linhaAtual.ehPortabilidade ? linhaAtual.portabilidadeCpf : undefined,
        portabilidadeOperadora: linhaAtual.ehPortabilidade ? linhaAtual.portabilidadeOperadora : undefined,
        portabilidadeLinha: linhaAtual.ehPortabilidade ? linhaAtual.portabilidadeLinha : undefined,
        portabilidadeCnpjCedente: linhaAtual.ehPortabilidade ? (linhaAtual.portabilidadeCnpjCedente || undefined) : undefined,
        portabilidadeRazaoSocialCedente: linhaAtual.ehPortabilidade
          ? (linhaAtual.portabilidadeRazaoSocialCedente || undefined)
          : undefined,
        portabilidadeRepresentantes:
          linhaAtual.ehPortabilidade && linhaAtual.portabilidadeRepresentantes.length > 0
            ? linhaAtual.portabilidadeRepresentantes.map((rep) => ({ ...rep }))
            : undefined,
        seraUsadaPeloCliente: linhaAtual.seraUsadaPeloCliente,
      });
    }

    setLinhaAtual({
      negociacao: 'Aditivo',
      ddd: '',
      numero: '',
      chipCcid: '',
      tipoVenda: 'Sem aparelho',
      planId: '',
      plano: '',
      aparelho: '',
      valorChip: fastchip ? 0 : 7.00,
      valorMensal: 0,
      quantidade: 1,
      ehPortabilidade: false,
      portabilidadeNomeCompleto: '',
      portabilidadeCpf: '',
      portabilidadeOperadora: '',
      portabilidadeLinha: '',
      portabilidadeCnpjCedente: '',
      portabilidadeRazaoSocialCedente: '',
      portabilidadeRepresentantes: [],
      seraUsadaPeloCliente: null,
    });
  };

  const handleAddLinha = () => {
    if (!onAddLinha) return;
    if (linhaAtual.negociacao === 'Renegociação' && (!linhaAtual.ddd || !linhaAtual.numero || linhaAtual.numero.length < 8)) {
      showError('DDD e número são obrigatórios para Renegociação (mín. 8 dígitos).');
      return;
    }
    if (linhaAtual.negociacao === 'Aditivo' && !linhaAtual.ddd) {
      showError('DDD é obrigatório para Aditivo.');
      return;
    }
    if (fastchip && linhaAtual.negociacao === 'Aditivo' && clientEstado && !validDdds.includes(linhaAtual.ddd)) {
      showError(`DDD ${linhaAtual.ddd} não é válido para o estado ${clientEstado} com Fastchip.`);
      return;
    }
    if (
      !linhaAtual.negociacao ||
      !linhaAtual.planId ||
      !hasContractLineMonthlyValue(linhaAtual.valorMensal)
    ) {
      showError('Preencha Plano e Valor mensal.');
      return;
    }
    if (
      teamContractRules.requireSeraUsadaPeloCliente &&
      linhaAtual.negociacao === 'Aditivo' &&
      linhaAtual.seraUsadaPeloCliente === null
    ) {
      showError('Por favor, informe se a linha será usada pelo cliente.');
      return;
    }
    if (linhaAtual.negociacao === 'Aditivo' && linhaAtual.ehPortabilidade) {
      const portErr = getPortabilidadeValidationError(linhaAtual);
      if (portErr) {
        showError(portErr);
        return;
      }

      const conflict = detectPortabilidadeConflict(linhas, linhaAtual);
      if (conflict.conflict) {
        setPortabilidadeConflict(conflict);
        setPendingAddAfterConflict(true);
        return;
      }
    }
    if (fastchip && linhaAtual.negociacao === 'Aditivo' && availableICcIdsCount !== undefined && linhaAtual.quantidade > availableICcIdsCount) {
      showError(`Não há ICCIDs suficientes para ${linhaAtual.quantidade} linhas. Disponíveis: ${availableICcIdsCount}.`);
      return;
    }
    commitAddLinha();
  };

  const handlePortabilidadeConflictConfirm = () => {
    if (!portabilidadeConflict?.conflict || !onAddLinha) {
      setPortabilidadeConflict(null);
      setPendingAddAfterConflict(false);
      return;
    }

    if (portabilidadeConflict.kind === 'tipo') {
      const converted = applyPortabilidadeTipoConversion(
        linhas,
        portabilidadeConflict.newTipo,
        linhaAtual
      );
      converted.forEach((linha) => {
        if (linha.id) {
          onUpdateLinha(linha.id, 'portabilidadeNomeCompleto', linha.portabilidadeNomeCompleto ?? '');
          onUpdateLinha(linha.id, 'portabilidadeCpf', linha.portabilidadeCpf ?? '');
        }
      });
      showSuccess(`${portabilidadeConflict.affectedCount} linha(s) convertida(s).`);
    } else if (portabilidadeConflict.kind === 'cedente') {
      const unified = applyCedenteUnification(linhas, linhaAtual);
      unified.forEach((linha) => {
        if (linha.id) {
          onUpdateLinha(linha.id, 'portabilidadeCnpjCedente', linha.portabilidadeCnpjCedente ?? '');
          onUpdateLinha(linha.id, 'portabilidadeRazaoSocialCedente', linha.portabilidadeRazaoSocialCedente ?? '');
          onUpdateLinha(linha.id, 'portabilidadeRepresentantes', linha.portabilidadeRepresentantes ?? []);
        }
      });
      showSuccess('Cedente unificado em todas as linhas CNPJ→CNPJ (cedente)');
    }

    setPortabilidadeConflict(null);
    if (pendingAddAfterConflict) {
      setPendingAddAfterConflict(false);
      commitAddLinha();
    }
  };

  const portabilidadeIncompleta =
    linhaAtual.negociacao === 'Aditivo' &&
    linhaAtual.ehPortabilidade &&
    isPortabilidadeIncompleta(linhaAtual, teamContractRules.requirePortabilidadeCompleta);

  const semSeraUsadaPeloCliente =
    teamContractRules.requireSeraUsadaPeloCliente &&
    linhaAtual.negociacao === 'Aditivo' &&
    (linhaAtual.seraUsadaPeloCliente === null || linhaAtual.seraUsadaPeloCliente === undefined);

  const fastchipSemCcidDisponivel = mode === 'edit' && fastchip && linhaAtual.negociacao === 'Aditivo' && availableICcIdsCount !== undefined && availableICcIdsCount === 0;
  const canAdd =
    linhaAtual.negociacao &&
    linhaAtual.ddd &&
    linhaAtual.planId &&
    hasContractLineMonthlyValue(linhaAtual.valorMensal) &&
    (linhaAtual.negociacao !== 'Renegociação' || (linhaAtual.numero && linhaAtual.numero.length >= 8)) &&
    !semSeraUsadaPeloCliente &&
    !portabilidadeIncompleta &&
    !fastchipSemCcidDisponivel;

  if (mode === 'create' && children) {
    return <>{children}</>;
  }

  const isMarkedForDeactivation = (linha: ContractLineEditorItem) =>
    !!(linha.excluirDoContrato || linha.requerSuspensao);

  return (
    <>
    <div>
      {hasCcidDuplicates && (
        <div
          style={{
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '15px',
            color: '#c62828',
          }}
        >
          <Icon name="warning" />
          <strong> CCIDs duplicados detectados!</strong>
          {duplicateCcidLines.length > 0 && (
            <span>
              {' '}
              ({duplicateCcidLines.length} linha{duplicateCcidLines.length > 1 ? 's' : ''} afetada
              {duplicateCcidLines.length > 1 ? 's' : ''}){' '}
            </span>
          )}
          Corrija os CCIDs duplicados antes de prosseguir.
        </div>
      )}

      {hasPhoneDuplicates && (
        <div
          style={{
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '15px',
            color: '#c62828',
          }}
        >
          <Icon name="warning" />
          <strong> Números de telefone duplicados detectados!</strong> Corrija os números duplicados antes de
          prosseguir.
        </div>
      )}

      <Table celled compact size="small">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Neg.</Table.HeaderCell>
            <Table.HeaderCell>DDD</Table.HeaderCell>
            <Table.HeaderCell>Número / CCID</Table.HeaderCell>
            <Table.HeaderCell>Plano</Table.HeaderCell>
            <Table.HeaderCell>Valor Mensal</Table.HeaderCell>
            <Table.HeaderCell width={1}>Port.</Table.HeaderCell>
            {allowLineMutations && <Table.HeaderCell width={1}></Table.HeaderCell>}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {linhas.map((linha, idx) => {
            const globalIndex = linhas.findIndex((l) => l === linha);
            const planosDaLinha = filterPlansForContractLine(
              plans,
              linha.negociacao || 'Aditivo',
              false
            );
            return (
              <React.Fragment key={linha.id || idx}>
                <Table.Row style={isMarkedForDeactivation(linha) ? { backgroundColor: '#fff5f5' } : undefined}>
                  <Table.Cell>
                    <select
                      value={linha.negociacao || 'Aditivo'}
                      disabled={readOnly}
                      onChange={(e) => onUpdateLinha(linha.id, 'negociacao', e.target.value)}
                      style={{ width: '100%', padding: '6px', borderRadius: 4, border: '1px solid rgba(34,36,38,.15)', fontSize: '12px' }}
                    >
                      <option value="Aditivo">Aditivo</option>
                      <option value="Renegociação">Renegociação</option>
                    </select>
                    {isMarkedForDeactivation(linha) && (
                      <Label size="mini" color="red" style={{ marginTop: 4, display: 'block' }}>
                        <Icon name="ban" />
                        Para desativar
                      </Label>
                    )}
                    {linha.ehPortabilidade && (
                      <Label size="mini" color="purple" style={{ marginTop: 4, display: 'block' }}>
                        <Icon name="exchange" />
                        Portabilidade
                      </Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {fastchip ? (
                      <select
                        value={linha.ddd || ''}
                        disabled={readOnly}
                        onChange={(e) => onUpdateLinha(linha.id, 'ddd', e.target.value)}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid rgba(34,36,38,.15)' }}
                      >
                        <option value="">DDD</option>
                        {validDdds.map((ddd) => (
                          <option key={ddd} value={ddd}>
                            {ddd}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Dropdown
                        placeholder="DDD"
                        selection
                        search
                        allowAdditions
                        disabled={readOnly}
                        additionLabel="Usar DDD: "
                        value={linha.ddd || ''}
                        options={dddOptions}
                        onAddItem={(e, { value }) => {
                          if (readOnly) return;
                          const dddValue = String(value).replace(/\D/g, '').slice(0, 2);
                          if (dddValue.length === 2) {
                            onAddDddOption?.(dddValue);
                            onUpdateLinha(linha.id, 'ddd', dddValue);
                          }
                        }}
                        onChange={(_, { value }) => {
                          if (readOnly) return;
                          onUpdateLinha(linha.id, 'ddd', String(value));
                        }}
                        style={{ minWidth: 70 }}
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {linha.negociacao === 'Renegociação' ? (
                      <div>
                        <Input
                          size="mini"
                          readOnly={readOnly}
                          placeholder="999999999"
                          value={renegociacaoLocalDigitsFromFormField(linha.ddd || '', linha.numero || '')}
                          onChange={(_, { value }) => {
                            if (readOnly) return;
                            const update = parseRenegociacaoNumeroFieldInput(linha.ddd || '', value);
                            if (update.ddd !== undefined) {
                              onAddDddOption?.(update.ddd);
                              onUpdateLinha(linha.id, 'ddd', update.ddd);
                            }
                            onUpdateLinha(linha.id, 'numero', update.numero);
                          }}
                          style={{
                            borderColor:
                              teamContractRules.blockDuplicatePhone &&
                              duplicatePhoneLines.includes(linha.id)
                                ? '#ff6b6b'
                                : undefined,
                          }}
                        />
                        {teamContractRules.blockDuplicatePhone && duplicatePhoneLines.includes(linha.id) && (
                          <div style={{ fontSize: '10px', color: '#ff6b6b', marginTop: '2px' }}>
                            Número duplicado — já está sendo usado em outra linha
                          </div>
                        )}
                        <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                          Digite apenas o número (até 9 dígitos, sem DDD)
                        </div>
                      </div>
                    ) : !fastchip ? (
                      <span style={{ fontSize: '11px', color: '#999' }}>
                        {linha.chipCcid || 'Não necessário'}
                      </span>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#155724' }} title={linha.chipCcid || ''}>
                        {linha.chipCcid ? `...${linha.chipCcid.slice(-8)}` : 'Auto'}
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <select
                      value={linha.planId || ''}
                      disabled={readOnly}
                      onChange={(e) => {
                        const selectedPlan = planosDaLinha.find(p => p.id === e.target.value);
                        onUpdateLinha(linha.id, 'planId', e.target.value);
                        onUpdateLinha(linha.id, 'plano', selectedPlan?.name || '');
                        onUpdateLinha(linha.id, 'planName', selectedPlan?.name || '');
                        onUpdateLinha(
                          linha.id,
                          'valorMensal',
                          resolvePlanTotalMonthlyPrice(selectedPlan?.totalMonthlyPrice)
                        );
                      }}
                      style={{ width: '100%', padding: '6px', borderRadius: 4, border: '1px solid rgba(34,36,38,.15)', fontSize: '11px' }}
                    >
                      <option value="">Selecione...</option>
                      {planosDaLinha.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {formatContractPlanOptionLabel(plan)}
                        </option>
                      ))}
                    </select>
                  </Table.Cell>
                  <Table.Cell>
                    <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                      R$ {Number(linha.valorMensal ?? linha.valorTotalMensal ?? 0).toFixed(2)}
                    </span>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {linha.negociacao === 'Aditivo' && (
                      <input
                        type="checkbox"
                        checked={linha.ehPortabilidade || false}
                        disabled={readOnly}
                        onChange={(e) => {
                          onUpdateLinha(linha.id, 'ehPortabilidade', e.target.checked);
                          if (!e.target.checked) {
                            onUpdateLinha(linha.id, 'portabilidadeNomeCompleto', '');
                            onUpdateLinha(linha.id, 'portabilidadeCpf', '');
                            onUpdateLinha(linha.id, 'portabilidadeOperadora', '');
                            onUpdateLinha(linha.id, 'portabilidadeLinha', '');
                            onUpdateLinha(linha.id, 'portabilidadeCnpjCedente', '');
                            onUpdateLinha(linha.id, 'portabilidadeRazaoSocialCedente', '');
                            onUpdateLinha(linha.id, 'portabilidadeRepresentantes', []);
                          }
                        }}
                        style={{ width: '16px', height: '16px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                        title="Marcar como portabilidade"
                      />
                    )}
                  </Table.Cell>
                  {allowLineMutations && (
                  <Table.Cell>
                    <Button size="mini" icon="trash" color="red" onClick={() => onRemoveLinha(linha.id)} />
                  </Table.Cell>
                  )}
                </Table.Row>
                {/* Sub-row: Tipo de Venda, Aparelho, Valor Chip, Será usada pelo cliente */}
                <Table.Row key={`${linha.id}-details`}>
                  <Table.Cell colSpan={7} style={{ background: '#fafafa', padding: '10px 16px', borderTop: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
                        <label style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Tipo de Venda</label>
                        <select
                          value={linha.tipoVenda || 'Sem aparelho'}
                          onChange={(e) => onUpdateLinha(linha.id, 'tipoVenda', e.target.value)}
                          disabled={readOnly || linha.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO'}
                          style={{
                            padding: '6px',
                            borderRadius: 4,
                            border: '1px solid rgba(34,36,38,.15)',
                            fontSize: '12px',
                            backgroundColor: readOnly || linha.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO' ? '#f5f5f5' : 'white'
                          }}
                        >
                          <option value="Sem aparelho">Sem aparelho</option>
                          <option value="Comodato">Comodato</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '200px' }}>
                        <label style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Aparelho</label>
                        <select
                          value={linha.aparelho || ''}
                          disabled={readOnly}
                          onChange={(e) => {
                            const aparelho = e.target.value;
                            onUpdateLinha(linha.id, 'aparelho', aparelho);
                            if (aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO') {
                              onUpdateLinha(linha.id, 'negociacao', 'Aditivo');
                              onUpdateLinha(linha.id, 'tipoVenda', 'Comodato');
                            }
                          }}
                          style={{
                            padding: '6px',
                            borderRadius: 4,
                            border: '1px solid rgba(34,36,38,.15)',
                            fontSize: '12px',
                            backgroundColor: readOnly ? '#f5f5f5' : 'white'
                          }}
                        >
                          <option value="">Nenhum</option>
                          <option value="ROTEADOR WTTX WNC WLD71T5B-PRETO">ROTEADOR WTTX WNC WLD71T5B-PRETO</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '100px' }}>
                        <label style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Valor Chip (R$)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={fastchip ? "0.00" : (linha.valorChip ?? 7)}
                          readOnly={readOnly || fastchip}
                          onChange={readOnly || fastchip ? undefined : (e) => onUpdateLinha(linha.id, 'valorChip', Number(e.target.value) || 0)}
                          style={{
                            padding: '6px',
                            borderRadius: 4,
                            border: '1px solid rgba(34,36,38,.15)',
                            fontSize: '12px',
                            width: '100px',
                            backgroundColor: readOnly || fastchip ? '#f5f5f5' : 'white',
                            cursor: readOnly || fastchip ? 'not-allowed' : 'text'
                          }}
                        />
                      </div>
                      {linha.negociacao === 'Aditivo' && (
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
                          <label style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>Será usada pelo cliente?</label>
                          <div
                            style={{
                              display: 'flex',
                              gap: '6px',
                              border:
                                teamContractRules.requireSeraUsadaPeloCliente &&
                                (linha.seraUsadaPeloCliente === null || linha.seraUsadaPeloCliente === undefined)
                                  ? '2px solid #ffc107'
                                  : '1px solid #ccc',
                              borderRadius: '4px',
                              padding: '4px',
                              backgroundColor:
                                teamContractRules.requireSeraUsadaPeloCliente &&
                                (linha.seraUsadaPeloCliente === null || linha.seraUsadaPeloCliente === undefined)
                                  ? '#fff3cd'
                                  : '#fff',
                            }}
                          >
                            <Button
                              size="mini"
                              type="button"
                              positive={linha.seraUsadaPeloCliente === true}
                              disabled={readOnly}
                              onClick={() => onUpdateLinha(linha.id, 'seraUsadaPeloCliente', true)}
                              style={{ fontWeight: linha.seraUsadaPeloCliente === true ? 'bold' : 'normal', fontSize: '11px' }}
                            >
                              Sim
                            </Button>
                            <Button
                              size="mini"
                              type="button"
                              negative={linha.seraUsadaPeloCliente === false}
                              disabled={readOnly}
                              onClick={() => onUpdateLinha(linha.id, 'seraUsadaPeloCliente', false)}
                              style={{ fontWeight: linha.seraUsadaPeloCliente === false ? 'bold' : 'normal', fontSize: '11px' }}
                            >
                              Não
                            </Button>
                          </div>
                          {teamContractRules.requireSeraUsadaPeloCliente &&
                            (linha.seraUsadaPeloCliente === null || linha.seraUsadaPeloCliente === undefined) && (
                            <div style={{ fontSize: '10px', color: '#e65100', marginTop: '2px', fontWeight: 'bold' }}>
                              Obrigatório
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
                {linha.negociacao === 'Aditivo' && linha.ehPortabilidade && (
                  <Table.Row key={`${linha.id}-port`}>
                    <Table.Cell colSpan={7} style={{ background: '#f9fcff', padding: '12px 24px', borderTop: '2px solid #2185d0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '140px' }}>
                          <Icon name="exchange" style={{ color: '#2185d0', margin: 0 }} />
                          <span style={{ fontWeight: 'bold', color: '#2185d0', fontSize: '11px' }}>Portabilidade:</span>
                        </div>
                        {resolvePortabilidadeTipo(linha) === 'cnpj-diferente' ? (
                          <>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px', paddingRight: '12px', borderRight: '1px solid #d0e7ff' }}>
                              <label style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>CNPJ Cedente</label>
                              <Input
                                size="mini"
                                placeholder="00.000.000/0000-00"
                                readOnly={readOnly}
                                maxLength={18}
                                value={linha.portabilidadeCnpjCedente || ''}
                                onChange={(_, { value }) => {
                                  if (readOnly) return;
                                  onUpdateLinha(linha.id, 'portabilidadeCnpjCedente', value);
                                }}
                                style={{ fontSize: '12px' }}
                              />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: '220px', flex: 1, paddingRight: '12px', borderRight: '1px solid #d0e7ff' }}>
                              <label style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Razão Social Cedente</label>
                              <Input
                                size="mini"
                                placeholder="Razão social da empresa cedente"
                                readOnly={readOnly}
                                value={linha.portabilidadeRazaoSocialCedente || ''}
                                onChange={(_, { value }) => {
                                  if (readOnly) return;
                                  onUpdateLinha(linha.id, 'portabilidadeRazaoSocialCedente', value);
                                }}
                                style={{ fontSize: '12px' }}
                              />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: '220px', paddingRight: '12px', borderRight: '1px solid #d0e7ff' }}>
                              <label style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>
                                Representante
                              </label>
                              <div style={{ fontSize: '11px', color: '#333' }}>
                                {(linha.portabilidadeRepresentantes || []).length === 0 ? (
                                  <span style={{ color: '#999' }}>Nenhum representante</span>
                                ) : (
                                  (() => {
                                    const rep = (linha.portabilidadeRepresentantes || [])[0];
                                    return (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>{rep.nome} — {rep.cpf}</span>
                                        {!readOnly && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              onUpdateLinha(linha.id, 'portabilidadeRepresentantes', [])
                                            }
                                            style={{ border: 'none', background: 'transparent', color: '#e53935', cursor: 'pointer', fontSize: '10px', padding: 0 }}
                                          >
                                            remover
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })()
                                )}
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: '220px', flex: 1, paddingRight: '12px', borderRight: '1px solid #d0e7ff' }}>
                              <label style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Nome Completo</label>
                              <Input
                                size="mini"
                                placeholder="Nome do portante"
                                readOnly={readOnly}
                                value={linha.portabilidadeNomeCompleto || ''}
                                onChange={(_, { value }) => {
                                  if (readOnly) return;
                                  onUpdateLinha(linha.id, 'portabilidadeNomeCompleto', value);
                                }}
                                style={{ fontSize: '12px' }}
                              />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: '150px', paddingRight: '12px', borderRight: '1px solid #d0e7ff' }}>
                              <label style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>CPF</label>
                              <Input
                                size="mini"
                                placeholder="000.000.000-00"
                                readOnly={readOnly}
                                maxLength={14}
                                value={linha.portabilidadeCpf || ''}
                                onChange={(_, { value }) => {
                                  if (readOnly) return;
                                  const v = value.replace(/\D/g, '').slice(0, 11);
                                  const fmt = v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
                                  onUpdateLinha(linha.id, 'portabilidadeCpf', fmt);
                                }}
                                style={{ fontSize: '12px' }}
                              />
                            </div>
                          </>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: '130px', paddingRight: '12px', borderRight: '1px solid #d0e7ff' }}>
                          <label style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Operadora</label>
                          <Input
                            size="mini"
                            placeholder="Ex: Vivo"
                            readOnly={readOnly}
                            value={linha.portabilidadeOperadora || ''}
                            onChange={(_, { value }) => {
                              if (readOnly) return;
                              onUpdateLinha(linha.id, 'portabilidadeOperadora', value);
                            }}
                            style={{ fontSize: '12px' }}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
                          <label style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Linha a Portar</label>
                          <Input
                            size="mini"
                            placeholder="DDD + Número"
                            readOnly={readOnly}
                            value={linha.portabilidadeLinha || ''}
                            onChange={(_, { value }) => {
                              if (readOnly) return;
                              onUpdateLinha(linha.id, 'portabilidadeLinha', toBrazilNationalPhoneDigits(value));
                            }}
                            style={{ fontSize: '12px' }}
                          />
                        </div>
                        {getPortabilidadeValidationError(linha) && (
                          <div style={{ fontSize: '10px', color: '#e53935', display: 'flex', alignItems: 'center', gap: '4px', minWidth: '160px' }}>
                            <Icon name="warning sign" style={{ margin: 0 }} />
                            {getPortabilidadeValidationError(linha)}
                          </div>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>

      {mode === 'edit' && onAddLinha && allowLineMutations && (
        <Segment style={{ marginTop: 16 }}>
          <Header as="h4" style={{ color: '#2c3e50', marginBottom: 12 }}>
            <Icon name="plus" />
            Adicionar Linha
          </Header>
          <Grid stackable>
            {/* Row 1: Negociação, DDD (condicional), CCID/Número (condicional), Aparelho */}
            <Grid.Row>
              <Grid.Column width={4}>
                <Form.Field required>
                  <label>Negociação</label>
                  <select
                    value={linhaAtual.negociacao}
                    onChange={(e) => {
                      if (linhaAtual.aparelho !== 'ROTEADOR WTTX WNC WLD71T5B-PRETO') {
                        setLinhaAtual((p) => ({ ...p, negociacao: e.target.value as 'Aditivo' | 'Renegociação' }));
                      }
                    }}
                    disabled={linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO'}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO' ? '#f5f5f5' : 'white',
                      cursor: linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO' ? 'not-allowed' : 'default'
                    }}
                  >
                    <option value="Aditivo">Aditivo</option>
                    <option value="Renegociação">Renegociação</option>
                  </select>
                  {linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO' && (
                    <div style={{ fontSize: '11px', color: '#2185d0', marginTop: '2px', fontWeight: 'bold' }}>
                      Tipo fixo para aparelho WTTX
                    </div>
                  )}
                </Form.Field>
              </Grid.Column>

              {linhaAtual.negociacao === 'Aditivo' && (
                <Grid.Column width={4}>
                  <Form.Field required>
                    <label>DDD</label>
                    {fastchip ? (
                      <>
                        <select
                          value={linhaAtual.ddd}
                          onChange={(e) => setLinhaAtual((p) => ({ ...p, ddd: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        >
                          <option value="">Selecione o DDD</option>
                          {validDdds.map((ddd) => (
                            <option key={ddd} value={ddd}>{ddd}</option>
                          ))}
                        </select>
                        {clientEstado && (
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                            DDDs disponíveis para {clientEstado} (Fastchip)
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <Dropdown
                          placeholder="Selecione ou digite o DDD"
                          selection
                          search
                          allowAdditions
                          additionLabel="Usar DDD: "
                          value={linhaAtual.ddd || ''}
                          options={dddOptions}
                          onAddItem={(e, { value }) => {
                            const dddValue = String(value).replace(/\D/g, '').slice(0, 2);
                            if (dddValue.length === 2) {
                              onAddDddOption?.(dddValue);
                              setLinhaAtual((p) => ({ ...p, ddd: dddValue }));
                            }
                          }}
                          onChange={(_, { value }) => {
                            const dddValue = String(value).replace(/\D/g, '').slice(0, 2);
                            setLinhaAtual((p) => ({ ...p, ddd: dddValue }));
                          }}
                          style={{ width: '100%' }}
                        />
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                          Selecione ou digite qualquer DDD de 2 dígitos
                        </div>
                      </>
                    )}
                  </Form.Field>
                </Grid.Column>
              )}

              {linhaAtual.negociacao === 'Renegociação' && (
                <>
                  <Grid.Column width={4}>
                    <Form.Field>
                      <label>DDD</label>
                      <Dropdown
                        placeholder="Selecione ou digite o DDD"
                        selection
                        search
                        allowAdditions
                        additionLabel="Usar DDD: "
                        value={linhaAtual.ddd || ''}
                        options={dddOptions}
                        onAddItem={(e, { value }) => {
                          const dddValue = String(value).replace(/\D/g, '').slice(0, 2);
                          if (dddValue.length === 2) {
                            onAddDddOption?.(dddValue);
                            setLinhaAtual((p) => ({ ...p, ddd: dddValue }));
                          }
                        }}
                        onChange={(_, { value }) => {
                          const dddValue = String(value).replace(/\D/g, '').slice(0, 2);
                          if (dddValue.length === 2) {
                            setLinhaAtual((p) => ({ ...p, ddd: dddValue }));
                          }
                        }}
                        style={{ width: '100%' }}
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Form.Field required>
                      <label>Número</label>
                      <input
                        type="text"
                        placeholder="999999999"
                        value={renegociacaoLocalDigitsFromFormField(linhaAtual.ddd || '', linhaAtual.numero || '')}
                        onChange={(e) => {
                          const update = parseRenegociacaoNumeroFieldInput(linhaAtual.ddd || '', e.target.value);
                          if (update.ddd !== undefined) {
                            onAddDddOption?.(update.ddd);
                            setLinhaAtual((prev) => ({ ...prev, ddd: update.ddd!, numero: update.numero }));
                          } else {
                            setLinhaAtual((prev) => ({ ...prev, numero: update.numero }));
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                        Digite apenas o número (até 9 dígitos, sem DDD)
                      </div>
                    </Form.Field>
                  </Grid.Column>
                </>
              )}

              {linhaAtual.negociacao === 'Aditivo' && (
                <Grid.Column width={4}>
                  <Form.Field>
                    <label>CCID</label>
                    {!fastchip ? (
                      <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px',
                                    backgroundColor: '#f5f5f5', color: '#666', fontSize: '12px' }}>
                        Não necessário (sem Fastchip)
                      </div>
                    ) : (
                      <div style={{ padding: '10px', border: '1px solid #28a745', borderRadius: '4px',
                                    color: '#155724', backgroundColor: '#d4edda', fontSize: '12px' }}>
                        Será selecionado automaticamente
                      </div>
                    )}
                  </Form.Field>
                </Grid.Column>
              )}

              <Grid.Column width={4}>
                <Form.Field>
                  <label>Aparelho</label>
                  <select
                    value={linhaAtual.aparelho}
                    onChange={(e) => {
                      const aparelho = e.target.value;
                      setLinhaAtual(prev => ({ 
                        ...prev, 
                        aparelho: aparelho,
                        negociacao: aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO' ? 'Aditivo' : prev.negociacao,
                        tipoVenda: aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO' ? 'Comodato' : prev.tipoVenda
                      }));
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Nenhum</option>
                    <option value="ROTEADOR WTTX WNC WLD71T5B-PRETO">ROTEADOR WTTX WNC WLD71T5B-PRETO</option>
                  </select>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    Selecione um aparelho se aplicável
                  </div>
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            {/* Row 2: Tipo de Venda, Plano, Valor Chip, Valor Mensal, Quantidade */}
            <Grid.Row>
              <Grid.Column width={3}>
                <Form.Field required>
                  <label>Tipo de Venda</label>
                  <select
                    value={linhaAtual.tipoVenda}
                    onChange={(e) => setLinhaAtual(prev => ({ ...prev, tipoVenda: e.target.value }))}
                    disabled={linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO'}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO' ? '#f5f5f5' : 'white',
                      cursor: linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO' ? 'not-allowed' : 'default'
                    }}
                  >
                    <option value="Sem aparelho">Sem aparelho</option>
                    <option value="Comodato">Comodato</option>
                  </select>
                  {linhaAtual.aparelho === 'ROTEADOR WTTX WNC WLD71T5B-PRETO' && (
                    <div style={{ fontSize: '11px', color: '#2185d0', marginTop: '2px', fontWeight: 'bold' }}>
                      Tipo fixo para aparelho WTTX
                    </div>
                  )}
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={4}>
                <Form.Field required>
                  <label>Plano</label>
                  <select
                    value={linhaAtual.planId}
                    onChange={(e) => {
                      const selectedPlan = plansFiltrados.find(p => p.id === e.target.value);
                      setLinhaAtual(prev => ({ 
                        ...prev, 
                        planId: e.target.value,
                        plano: selectedPlan?.name || '',
                        valorMensal: resolvePlanTotalMonthlyPrice(selectedPlan?.totalMonthlyPrice)
                      }));
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    disabled={plansLoading}
                  >
                    <option value="">Selecione um plano...</option>
                    {plansFiltrados.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {formatContractPlanOptionLabel(plan)}
                      </option>
                    ))}
                  </select>
                  {plansLoading && (
                    <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                      <Icon name="spinner" loading />
                      Carregando planos...
                    </div>
                  )}
                  {!plansLoading && plansFiltrados.length === 0 && plans.length > 0 && (
                    <div style={{ marginTop: '5px', fontSize: '12px', color: '#ff6b6b' }}>
                      Nenhum plano disponível para {linhaAtual.negociacao}
                    </div>
                  )}
                  {!plansLoading && plans.length === 0 && (
                    <div style={{ marginTop: '5px', fontSize: '12px', color: '#ff6b6b' }}>
                      Nenhum plano disponível
                    </div>
                  )}
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={3}>
                <Form.Field required>
                  <label>Valor Chip (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="7,00"
                    value={fastchip ? "0.00" : linhaAtual.valorChip}
                    readOnly={fastchip}
                    onChange={fastchip ? undefined : (e) => setLinhaAtual(prev => ({ ...prev, valorChip: Number(e.target.value) || 0 }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: fastchip ? '#f5f5f5' : 'white',
                      cursor: fastchip ? 'not-allowed' : 'text'
                    }}
                  />
                  {fastchip && (
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                      Valor fixo para Fastchip: R$ 0,00
                    </div>
                  )}
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={3}>
                <Form.Field required>
                  <label>Valor Mensal (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="0,00"
                    value={linhaAtual.valorMensal || ''}
                    readOnly
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed'
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    Valor preenchido automaticamente pelo plano
                  </div>
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={3}>
                <Form.Field required>
                  <label>Quantidade</label>
                  <QuantitySelector
                    value={linhaAtual.quantidade}
                    onChange={(value) => setLinhaAtual(prev => ({ ...prev, quantidade: value }))}
                    min={1}
                    max={99}
                  />
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
                    Número de linhas a criar
                  </div>
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            {/* Row 3: Checkbox Portabilidade (somente Aditivo) */}
            {linhaAtual.negociacao === 'Aditivo' && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      id="ehPortabilidadeEditor"
                      checked={linhaAtual.ehPortabilidade}
                      onChange={e => {
                        const checked = e.target.checked;
                        if (checked) {
                          const ultimaPortabilidade = [...linhas]
                            .reverse()
                            .find((l: any) => isPortabilidadeAditivoLine(l));
                          const copied = ultimaPortabilidade
                            ? copyPortabilidadeFieldsFromLine(ultimaPortabilidade)
                            : {};
                          setLinhaAtual(p => ({
                            ...p,
                            ehPortabilidade: true,
                            portabilidadeLinha: '',
                            ...copied,
                          }));
                        } else {
                          setLinhaAtual(p => ({
                            ...p,
                            ehPortabilidade: false,
                            portabilidadeNomeCompleto: '',
                            portabilidadeCpf: '',
                            portabilidadeOperadora: '',
                            portabilidadeLinha: '',
                            portabilidadeCnpjCedente: '',
                            portabilidadeRazaoSocialCedente: '',
                            portabilidadeRepresentantes: [],
                          }));
                        }
                      }}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="ehPortabilidadeEditor" style={{ fontWeight: 'bold', color: '#2c3e50', fontSize: '15px', cursor: 'pointer', marginBottom: 0 }}>
                      É portabilidade?
                    </label>
                  </div>
                </Grid.Column>
              </Grid.Row>
            )}

            {/* Row 4: Dados da Portabilidade (somente Aditivo + ehPortabilidade) */}
            {linhaAtual.negociacao === 'Aditivo' && linhaAtual.ehPortabilidade && (
              <>
                {linhas.some((l: any) => isPortabilidadeAditivoLine(l)) && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Form.Field>
                        <label style={{ fontSize: '13px' }}>Usar dados de portabilidade de…</label>
                        <select
                          value=""
                          onChange={(e) => {
                            const sourceId = e.target.value;
                            if (!sourceId) return;
                            const source = linhas.find((l: any) => l.id === sourceId);
                            if (!source || !isPortabilidadeAditivoLine(source)) return;
                            setLinhaAtual((p) => ({
                              ...p,
                              ...copyPortabilidadeFieldsFromLine(source),
                            }));
                          }}
                          style={{ width: '100%', maxWidth: '420px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        >
                          <option value="">Selecione uma linha…</option>
                          {linhas.map((l: any, i) =>
                            isPortabilidadeAditivoLine(l) ? (
                              <option key={l.id ?? i} value={l.id}>
                                {[l.ddd, l.numero].filter(Boolean).join(' ') || `Linha ${i + 1}`} —{' '}
                                {l.portabilidadeNomeCompleto || l.portabilidadeRazaoSocialCedente || 'Portabilidade'}
                              </option>
                            ) : null
                          )}
                        </select>
                      </Form.Field>
                    </Grid.Column>
                  </Grid.Row>
                )}
                <Grid.Row>
                  <Grid.Column width={16}>
                    <PortabilidadeDadosForm
                      linha={linhaAtual}
                      outrasLinhas={linhas}
                      onChange={(field, value) => setLinhaAtual((p) => ({ ...p, [field]: value }))}
                      onUnificarPortanteEmTodas={(source) => {
                        const unified = applyPortanteUnification(linhas, source);
                        unified.forEach((linha) => {
                          if (linha.id) {
                            onUpdateLinha(linha.id, 'portabilidadeNomeCompleto', linha.portabilidadeNomeCompleto ?? '');
                            onUpdateLinha(linha.id, 'portabilidadeCpf', linha.portabilidadeCpf ?? '');
                          }
                        });
                        setLinhaAtual((p) => ({
                          ...p,
                          portabilidadeNomeCompleto: source.portabilidadeNomeCompleto ?? '',
                          portabilidadeCpf: source.portabilidadeCpf ?? '',
                        }));
                        showSuccess('Titular PF→CNPJ aplicado em todas as linhas compatíveis.');
                      }}
                    />
                  </Grid.Column>
                </Grid.Row>
              </>
            )}

            {/* Row 5: Será usada pelo cliente (somente Aditivo) */}
            {linhaAtual.negociacao === 'Aditivo' && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <Form.Field required={teamContractRules.requireSeraUsadaPeloCliente}>
                    <label style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '8px', display: 'block', fontSize: '15px' }}>
                      Será usada pelo cliente?
                    </label>
                    <div style={{ 
                      display: 'flex', 
                      gap: '10px',
                      border: teamContractRules.requireSeraUsadaPeloCliente && linhaAtual.seraUsadaPeloCliente === null ? '2px solid #ffc107' : '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '8px',
                      backgroundColor: teamContractRules.requireSeraUsadaPeloCliente && linhaAtual.seraUsadaPeloCliente === null ? '#fff3cd' : '#fff',
                      maxWidth: '400px'
                    }}>
                      <Button
                        type="button"
                        positive={linhaAtual.seraUsadaPeloCliente === true}
                        onClick={() => setLinhaAtual(prev => ({ ...prev, seraUsadaPeloCliente: true }))}
                        style={{
                          flex: 1,
                          fontWeight: linhaAtual.seraUsadaPeloCliente === true ? 'bold' : 'normal',
                          fontSize: '14px'
                        }}
                      >
                        Sim
                      </Button>
                      <Button
                        type="button"
                        negative={linhaAtual.seraUsadaPeloCliente === false}
                        onClick={() => setLinhaAtual(prev => ({ ...prev, seraUsadaPeloCliente: false }))}
                        style={{
                          flex: 1,
                          fontWeight: linhaAtual.seraUsadaPeloCliente === false ? 'bold' : 'normal',
                          fontSize: '14px'
                        }}
                      >
                        Não
                      </Button>
                    </div>
                    {teamContractRules.requireSeraUsadaPeloCliente && linhaAtual.seraUsadaPeloCliente === null && (
                      <div style={{ fontSize: '11px', color: '#ffc107', marginTop: '4px', fontWeight: 'bold' }}>
                        Selecione uma opção (obrigatório)
                      </div>
                    )}
                    {linhaAtual.seraUsadaPeloCliente !== null && (
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                        {linhaAtual.seraUsadaPeloCliente ? 'Linha será usada pelo cliente' : 'Linha não será usada pelo cliente'}
                      </div>
                    )}
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
            )}

            {/* Row 6: Botão Adicionar (ÚLTIMA row, sozinho) */}
            <Grid.Row>
              <Grid.Column width={16}>
                <Button
                  color="green"
                  onClick={handleAddLinha}
                  disabled={!canAdd}
                  size="large"
                >
                  <Icon name="plus" />
                  Adicionar Linha
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      )}
    </div>
    <PortabilidadeConflictModal
      open={!!portabilidadeConflict?.conflict}
      conflict={portabilidadeConflict}
      onConfirm={handlePortabilidadeConflictConfirm}
      onCancel={() => {
        setPortabilidadeConflict(null);
        setPendingAddAfterConflict(false);
      }}
    />
    </>
  );
};

export default ContractLinesEditor;
